import axios from '../lib/axios';
import { AxiosError } from 'axios';
import { Permission } from '../types/auth';

interface LoginResponse {
  status: string;
  result: {
    otpHash: string;
  };
}

interface ErrorResponse {
  message: string;
  statusCode: number;
}

interface VerifyOtpResponse {
  status: string;
  result: {
    _id?: string;
    token: string;
    name: string;
    code: string;
    email: string;
    isActive: boolean;
    lastLogin: string;
    mobileNo: string;
    dashPermissions?: Permission[];
  };
}

export const login = async (
  mobileNo: string
): Promise<{ otpHash: string }> => {
  try {
    console.log('[api] Sending login request for:', mobileNo);

    const response = await axios.post<LoginResponse>('/auth/v3/login', {
      mobileNo,
    });

    console.log('[api] Full login response:', response.data);

    if (
      response.data.status === 'Request successful!' &&
      response.data.result.otpHash
    ) {
      return {
        otpHash: response.data.result.otpHash,
      };
    }

    throw new Error('Invalid response from server');
  } catch (error) {
    const axiosError = error as AxiosError<any>;

    if (axiosError.response && axiosError.response.data) {
      const apiData = axiosError.response.data;

      const message =
        apiData.message ||
        apiData.result ||
        'Login failed. Please try again.';

      console.error('[api] API Error:', apiData);
      throw new Error(message);
    }

    console.error('[api] Network Error:', error);

    throw new Error(
      'Unable to connect to the server. Please check your internet connection.'
    );
  }
};

export const verifyOtp = async (
  mobileNo: string,
  otp: string,
  otpHash: string
) => {
  console.log('[api] Making verify OTP request with:', {
    mobileNo,
    otp,
    otpHash: otpHash
      ? `${otpHash.substring(0, 15)}...`
      : 'missing',
  });

  try {
    if (!mobileNo || !otp || !otpHash) {
      console.error(
        '[api] Missing required params for OTP verification'
      );

      throw new Error(
        'Missing required parameters for OTP verification'
      );
    }

    const otpString = String(otp).trim();

    if (!/^\d+$/.test(otpString)) {
      throw new Error('OTP must contain only numbers');
    }

    const requestBody = {
      type: 'lender',
      mobileNo,
      otp: otpString,
      otpHash,
    };

    console.log(
      '[api] Sending OTP verification request with body:',
      {
        ...requestBody,
        otpHash:
          requestBody.otpHash.substring(0, 15) + '...',
      }
    );

    const response = await axios.post<VerifyOtpResponse>(
      '/auth/v3/verify',
      requestBody
    );

    console.log(
      '[api] Verify OTP response:',
      response.data
    );

    if (response.data.status !== 'Request successful!') {
      throw new Error(
        'OTP verification failed. Please try again.'
      );
    }

    if (
      !response.data.result ||
      !response.data.result.token
    ) {
      throw new Error(
        'Invalid response from server: Missing required data'
      );
    }

    console.log(
      '[api] Raw API response result:',
      response.data.result
    );

    const {
      token,
      name,
      email,
      lastLogin,
      code,
      isActive,
      dashPermissions,
    } = response.data.result;

    const _id =
      response.data.result._id ||
      email ||
      mobileNo ||
      '';

    const userData = {
      token,
      user: {
        _id,
        name,
        email,
        mobileNo,
        lastLogin,
        code,
        isActive,
        dashPermissions,
      },
    };

    console.log(
      '[DEBUG] Final constructed user data:',
      userData
    );

    console.log(
      '[DEBUG] Dashboard permissions:',
      dashPermissions
    );

    return userData;
  } catch (error) {
    console.error('[api] Verify OTP error:', error);

    const axiosError =
      error as AxiosError<ErrorResponse>;

    if (
      axiosError.response &&
      axiosError.response.data
    ) {
      const errData =
        axiosError.response.data as any;

      console.error(
        '[api] API Error response:',
        errData
      );

      if (errData.result) {
        throw new Error(errData.result);
      }

      if (errData.message) {
        throw new Error(errData.message);
      }

      throw new Error(
        'OTP verification failed. Please try again.'
      );
    }

    if (error instanceof Error) {
      throw error;
    }

    throw new Error(
      'Unable to connect to the server. Please check your internet connection.'
    );
  }
};

export const isValidMobileNumber = (
  mobileNo: string
): boolean => {
  const pattern = /^[6-9]\d{9}$/;
  return pattern.test(mobileNo);
};